import EventNoteIcon from "@mui/icons-material/EventNote";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import {
  BooleanField,
  Create,
  CreateButton,
  Datagrid,
  DateInput,
  Edit,
  FunctionField,
  List,
  SimpleForm,
  TextField,
  TextInput,
  TopToolbar,
  Toolbar,
  SaveButton,
  required,
  usePermissions,
  useRecordContext,
  type CreateProps,
  type EditProps,
  type ListProps,
} from "react-admin";
import CustomEmpty from "@/components/custom-empty";
import { DisplayDateField } from "@/components/DisplayDateField";
import { hasPermission } from "@/utils/permission-utils";
import { MADRASA_SETTINGS_EDIT } from "../shared/madrasa-permissions";
import { RecordTitle } from "../shared/RecordTitle";
import { AcademicYearActions } from "./AcademicYearActions";
import { suggestIndianAcademicYear, validateAcademicYearDates } from "./academic-year-defaults";

type AcademicYearRecord = { isCurrent?: boolean; isClosed?: boolean };

const validateForm = (values: { startDate?: string; endDate?: string }) => {
  const error = validateAcademicYearDates(values);
  return error ? { endDate: error } : {};
};

function AcademicYearFields() {
  return (
    <>
      <TextInput
        source="name"
        label="Name"
        validate={required()}
        helperText='Usually "2026-27".'
        fullWidth
      />
      <DateInput
        source="startDate"
        label="Start date"
        validate={required()}
        helperText="Standard Indian academic year starts 1 April."
        fullWidth
      />
      <DateInput
        source="endDate"
        label="End date"
        validate={required()}
        helperText="Standard Indian academic year ends 31 March."
        fullWidth
      />
    </>
  );
}

function StatusField() {
  return (
    <FunctionField
      label="Status"
      render={(record: AcademicYearRecord) => (
        <>
          {record.isCurrent && (
            <Chip size="small" color="success" label="Current" sx={{ mr: 0.5 }} />
          )}
          {record.isClosed && <Chip size="small" label="Closed" />}
          {!record.isCurrent && !record.isClosed && "—"}
        </>
      )}
    />
  );
}

const ListActions = () => {
  const { permissions } = usePermissions();
  return (
    <TopToolbar>{hasPermission(permissions, MADRASA_SETTINGS_EDIT) && <CreateButton />}</TopToolbar>
  );
};

export function AcademicYearsList(props: ListProps) {
  return (
    <List
      {...props}
      sort={{ field: "startDate", order: "DESC" }}
      perPage={25}
      actions={<ListActions />}
      empty={
        <CustomEmpty
          title="No academic years yet"
          subtitle="Create your first academic year to start enrolling students and collecting fees."
          action={<CreateButton variant="contained" />}
        />
      }
    >
      <Datagrid rowClick="edit" bulkActionButtons={false}>
        <TextField source="name" label="Name" />
        <DisplayDateField source="startDate" label="Starts" />
        <DisplayDateField source="endDate" label="Ends" />
        <StatusField />
        <AcademicYearActions />
      </Datagrid>
    </List>
  );
}

export function AcademicYearsCreate(props: CreateProps) {
  return (
    <Create {...props} redirect="list" title="Add academic year">
      <SimpleForm
        warnWhenUnsavedChanges
        sx={{ maxWidth: 500 }}
        defaultValues={() => suggestIndianAcademicYear()}
        validate={validateForm}
      >
        <AcademicYearFields />
      </SimpleForm>
    </Create>
  );
}

function ClosedNotice() {
  const record = useRecordContext<AcademicYearRecord>();
  if (!record?.isClosed) return null;
  return (
    <Alert severity="info" sx={{ mb: 2 }}>
      This academic year is closed. Reopen it from the list to make changes.
    </Alert>
  );
}

function EditForm() {
  const record = useRecordContext<AcademicYearRecord>();
  return (
    <SimpleForm
      warnWhenUnsavedChanges
      sx={{ maxWidth: 500 }}
      validate={validateForm}
      // Academic years cannot be deleted (close them instead), so the form offers Save only.
      toolbar={
        <Toolbar>
          <SaveButton />
        </Toolbar>
      }
    >
      <ClosedNotice />
      <fieldset
        disabled={!!record?.isClosed}
        style={{ border: 0, padding: 0, margin: 0, width: "100%" }}
      >
        <AcademicYearFields />
      </fieldset>
      <BooleanField source="isCurrent" label="Current year" />
    </SimpleForm>
  );
}

export function AcademicYearsEdit(props: EditProps) {
  return (
    <Edit
      {...props}
      mutationMode="pessimistic"
      title={<RecordTitle prefix="Edit academic year" sources={["name"]} />}
    >
      <EditForm />
    </Edit>
  );
}

export default {
  list: AcademicYearsList,
  create: AcademicYearsCreate,
  edit: AcademicYearsEdit,
  icon: EventNoteIcon,
  label: "Academic years",
  options: { label: "Academic years" },
  name: "academicYears",
};
